import type { Dayjs } from 'dayjs';
import type { Game } from './games/GamesResponse';
import { gameConferences, type Conference } from './conferences';
import { isSoccerLeague } from './scheduleConfig';
import type { Event } from './events/EventsResponse';
import type { Race } from './races/RacesResponse';
import {
    buildSideListenOptions,
    resolveHeadlineChannel,
    resolvePlayerUrl,
    type ChannelStreams,
    type HeadlineChannel,
    type ListenOption,
} from './channelWaterfall';
import { formatStartTime, groupByDay, isLive, isWithin, hasEnded, type DateRange, type ScheduleDay } from './schedule';

export interface ScheduleEntity {
    name: string;
    logo?: AEMImage;
    href?: string;
    description?: string;
    color?: string;
    headline?: HeadlineChannel;
    listenOptions: ListenOption[];
    playerUrl?: string;
}

export interface ScheduleDriver {
    name: string;
    channel: number;
    deepLink?: string;
}

interface ScheduleItemBase {
    key: string;
    start: string;
    end?: string;
    tbd?: boolean;
}

export type ScheduleItem = ScheduleItemBase &
    (
        | {
              variant: 'matchup';
              awayTeam: ScheduleEntity;
              homeTeam: ScheduleEntity;
              separator: 'at' | 'versus';
              conferences: Conference[];
          }
        | { variant: 'event'; event: ScheduleEntity; drivers: ScheduleDriver[] }
    );

export type ScheduleRow = ScheduleItem & { startTime: string; isLive: boolean };

function joinName(...parts: (string | undefined)[]): string {
    return parts.filter(Boolean).join(' ');
}

function teamEntity(game: Game, side: 'away' | 'home'): ScheduleEntity {
    const teams = {
        away: {
            city: game.awayCity,
            team: game.awayTeam,
            logo: game.awayLogo,
            href: game.awayUrl,
            color: game.awayColor,
        },
        home: {
            city: game.homeCity,
            team: game.homeTeam,
            logo: game.homeLogo,
            href: game.homeUrl,
            color: game.homeColor,
        },
    };
    const { city, team, ...details } = teams[side];
    const name = joinName(city, team);

    return {
        ...details,
        name,
        headline: resolveHeadlineChannel(game, side),
        listenOptions: buildSideListenOptions(game, side, name),
        playerUrl: resolvePlayerUrl(game, side),
    };
}

function eventEntity(
    streams: ChannelStreams,
    details: Pick<ScheduleEntity, 'name' | 'logo' | 'href' | 'description'>,
    playerUrl?: string,
): ScheduleEntity {
    return {
        ...details,
        headline: resolveHeadlineChannel(streams, 'national'),
        listenOptions: buildSideListenOptions(streams, 'national'),
        playerUrl: playerUrl || resolvePlayerUrl(streams, 'national'),
    };
}

export function gameToScheduleItem(game: Game, { isSoccer }: { isSoccer: boolean }): ScheduleItem {
    const separators = { true: 'versus', false: 'at' } as const;

    return {
        key: `game-${game.gameID}`,
        start: game.gameDate,
        end: game.endDateTime,
        tbd: game.tbdTime,
        variant: 'matchup',
        awayTeam: teamEntity(game, 'away'),
        homeTeam: teamEntity(game, 'home'),
        separator: separators[`${isSoccer}`],
        conferences: gameConferences(game, { includeLeagues: isSoccer }),
    };
}

export function eventToScheduleItem(event: Event, playerUrl?: string): ScheduleItem {
    return {
        key: `event-${event.gameID}`,
        start: event.gameDate,
        end: event.gameEndDate,
        variant: 'event',
        event: eventEntity(
            event,
            { name: event.eventName ?? '', description: event.venueName, logo: event.homeLogo, href: event.homeUrl },
            playerUrl,
        ),
        drivers: [],
    };
}

function toDriver(driver: Race): ScheduleDriver | undefined {
    const channel = driver.nationalInternetStream || driver.nationalStream;
    if (!channel) return undefined;
    return { name: driver.raceOrDriverName, channel, deepLink: driver.nationalStreamLink };
}

export function raceToScheduleItem(race: Race, playerUrl?: string): ScheduleItem {
    return {
        key: `race-${race.raceID}-${race.raceDriverID}`,
        start: race.startDateTime,
        end: race.endDateTime,
        variant: 'event',
        event: eventEntity(
            race,
            {
                name: race.raceOrDriverName || race.seriesName,
                description: race.venueOrTeamName,
                logo: race.homeLogo,
                href: race.homeUrl,
            },
            playerUrl,
        ),
        drivers: (race.raceDrivers ?? []).map(toDriver).filter((driver) => driver !== undefined),
    };
}

export function upcomingItems(items: ScheduleItem[], now: Dayjs, timeZone: string): ScheduleItem[] {
    return items.filter((item) => !hasEnded(item, now, timeZone));
}

export function buildScheduleDays(
    items: ScheduleItem[],
    { now, timeZone, range }: { now: Dayjs; timeZone: string; range?: DateRange },
): ScheduleDay<ScheduleRow>[] {
    const rows = upcomingItems(items, now, timeZone)
        .filter((item) => !range || isWithin(item.start, range))
        .map((item) => ({
            ...item,
            startTime: formatStartTime(item.start, timeZone, item.tbd),
            isLive: isLive(item, now),
        }));

    return groupByDay(rows, (row) => row.start, timeZone);
}

export type ScheduleKind = 'races' | 'events' | 'games';

interface ScheduleSource {
    abbreviation: string;
    games: Game[];
    events: Event[];
    races: Race[];
}

const SCHEDULE_BUILDERS: {
    kind: ScheduleKind;
    build: (source: ScheduleSource, playerUrl?: string) => ScheduleItem[];
}[] = [
    { kind: 'races', build: (source, playerUrl) => source.races.map((race) => raceToScheduleItem(race, playerUrl)) },
    {
        kind: 'events',
        build: (source, playerUrl) => source.events.map((event) => eventToScheduleItem(event, playerUrl)),
    },
    {
        kind: 'games',
        build: (source) => {
            const isSoccer = isSoccerLeague(source.abbreviation.toLowerCase());
            return source.games.map((game) => gameToScheduleItem(game, { isSoccer }));
        },
    },
];

export function buildLeagueSchedule(
    source: ScheduleSource,
    playerUrl?: string,
): { kind: ScheduleKind; items: ScheduleItem[] } | undefined {
    const builder = SCHEDULE_BUILDERS.find(({ kind }) => source[kind].length > 0);
    if (!builder) return undefined;
    return { kind: builder.kind, items: builder.build(source, playerUrl) };
}
